import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Ticket, PackageOpen, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, brl, errMsg, imgUrl } from "@/lib/api";
import { fmtPts } from "@/lib/loyalty";
import { cn } from "@/lib/utils";
import { RewardDialog } from "./RewardDialog";
import { toast } from "sonner";

function RewardCard({ r, onEdit, onDelete, onToggle }) {
  return (
    <div className={cn("flex items-center gap-3 rounded-xl border p-3 transition-opacity", !r.active && "opacity-50")} data-testid={`reward-card-${r.id}`}>
      {r.image_url ? <img src={imgUrl(r.image_url)} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />
        : <span className="grid h-16 w-16 shrink-0 place-items-center rounded-lg bg-amber-500/15 text-amber-500"><Ticket className="h-6 w-6" /></span>}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">{r.name}</p>
        {r.type === "product" && <p className="text-sm text-muted-foreground">{brl(r.value)}</p>}
        <p className="text-sm">A partir de <b>{fmtPts(r.points)}</b></p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <Switch checked={r.active} onCheckedChange={() => onToggle(r)} aria-label="Ativa" data-testid={`reward-active-${r.id}`} />
        <div className="flex gap-1">
          <button onClick={() => onEdit(r)} className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label="Editar" data-testid={`reward-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={() => onDelete(r)} className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-red-500" aria-label="Excluir" data-testid={`reward-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

function Group({ title, type, items, onAdd, ...h }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="card-surface overflow-hidden" data-testid={`rewards-group-${type}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-3 font-display text-lg font-semibold" data-testid={`rewards-toggle-${type}`}>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}{title}<span className="text-sm font-normal text-muted-foreground">({items.length})</span>
        </button>
        <Button onClick={() => onAdd(type)} data-testid={`rewards-add-${type}`}><Plus className="mr-1 h-4 w-4" />{type === "discount" ? "Adicionar desconto" : "Adicionar produto"}</Button>
      </div>
      {open && (items.length ? <div className="grid gap-3 p-5 md:grid-cols-2">{items.map((r) => <RewardCard key={r.id} r={r} {...h} />)}</div> : (
        <div className="flex flex-col items-center gap-2 px-5 py-10 text-center text-muted-foreground" data-testid={`rewards-empty-${type}`}>
          <PackageOpen className="h-10 w-10 opacity-50" /><p className="text-sm">{type === "discount" ? "Nenhum desconto cadastrado" : "Nenhum produto cadastrado"}</p>
        </div>
      ))}
    </section>
  );
}

export function RewardsSection() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/loyalty/rewards", { params: { all: true } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const h = {
    onEdit: setEdit,
    onDelete: (r) => window.confirm(`Excluir a recompensa "${r.name}"?`) && run(() => api.delete(`/loyalty/rewards/${r.id}`), "Recompensa excluída"),
    onToggle: (r) => run(() => api.put(`/loyalty/rewards/${r.id}`, { type: r.type, points: r.points, value: r.value, product_id: r.product_id, active: !r.active }),
      r.active ? "Recompensa pausada" : "Recompensa ativada"),
  };
  return (
    <div className="space-y-4" data-testid="rewards-section">
      <Group title="Recompensa em descontos" type="discount" items={rows.filter((r) => r.type === "discount")} onAdd={(type) => setEdit({ type })} {...h} />
      <Group title="Recompensa em produtos" type="product" items={rows.filter((r) => r.type === "product")} onAdd={(type) => setEdit({ type })} {...h} />
      <RewardDialog reward={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
